
























































































































import { Vue, Component, Prop, Mixins, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { OutputSearch } from "@/store/site-monitor-cameras/types";
import MatrixSidebarMixin from "./MatrixSidebarMixin";
import { DeviceDetails, DeviceService } from "@sureview/v2-cameras";
import { debounce } from "lodash";

import { stringMixin } from "@/mixins";
import { renderDistance } from "@/filters";

const { isNullOrWhitespace } = stringMixin.methods;

const SiteMonitor = namespace("siteMonitor");
const SiteMonitorCameras = namespace("siteMonitorCameras");
const Devices = namespace("devices");

type SelectedPulseOutputMap = {
	[outputId: number]: {
		reason: string;
		error: string;
		sendingCommand: boolean;
	};
};

@Component({
	filters: {
		renderDistance,
	},
})
export default class DeviceList extends Mixins(MatrixSidebarMixin) {
	@Devices.Action private deviceAction: ({ deviceID, payload }) => Promise<DeviceDetails>;

	@SiteMonitor.Mutation private setActivity: () => void;

	@SiteMonitorCameras.Getter("getDeviceControllerOutputs") private outputs!: any[];
	@SiteMonitorCameras.Getter private searchedOutputs: OutputSearch[];
	@SiteMonitorCameras.Action private searchForOutputs: ({ query, groupID }) => Promise<void>;

	private outputSearchQuery: string = "";
	private selectedPulseOutputs: SelectedPulseOutputMap = {};

	/**
	 * Watches the output search query and peforms a server-side search.
	 */
	@Watch("outputSearchQuery")
	private async onOutputSearchQueryChanged(query: string) {
		await this.findOutputs(query);
	}

	/**
	 * Performs a search on the server side when our output query changes.
	 */
	private findOutputs = debounce(async function (query: string) {
		let groupID = this.eventDetails.groupID;
		await this.searchForOutputs({ query, groupID });
	}, 300);

	/**
	 * Handler for when an output is clicked (selected).
	 *
	 * -	If the output is not currently selected, it sets it's value in the
	 * 		selectedPulseOutput map to be true; which opens up a form with
	 * 		which the user can send a command to an output.
	 *
	 * - 	If the output is currently selected, it delete's it's value in the
	 * 		selectedPulseOutput map. This closes the 'pulse output' form.
	 */
	private setOutputSelected(output: OutputSearch, selected: boolean) {
		if (selected) {
			Vue.set(this.selectedPulseOutputs, output.objectID, {
				reason: "",
				error: null,
				sendingCommand: false,
			});
		} else {
			Vue.delete(this.selectedPulseOutputs, output.objectID);
		}
	}

	/**
	 * Returns true if the given output is currently 'selected' (has a value in the
	 * selectedPulseOutput map).
	 */
	private isOutputSelected(output: OutputSearch): boolean {
		return this.selectedPulseOutputs[output.objectID] != null;
	}

	/**
	 * Actually pulses an output using the data entered into the form by the user.
	 */
	private async doPulseOutput(output: OutputSearch) {
		let outputPulseState = this.selectedPulseOutputs[output.objectID];

		// If we have no reason, or we're already pulsing the output, fail fast.
		if (isNullOrWhitespace(outputPulseState.reason) || outputPulseState.sendingCommand) {
			return;
		}

		// Notify the Site Monitor that the user has performed an activity.
		this.setActivity();

		// Mark this output as currently being pulsed.
		outputPulseState.sendingCommand = true;

		// Audit our device interaction
		const device = await this.deviceAction({
			deviceID: output.objectID,
			payload: {
				eventId: this.eventId,
				deviceAction: "pulse",
				outputReason: outputPulseState.reason,
			},
		});

		// Create a connection with the device service that handles the current output,
		// and send it a pulse command. If an error occurs we add it to the selectedPulseOutput
		// so it can be displayed to the user.
		let auth = this.getSession("3");
		let deviceService = new DeviceService(device.deviceServerEndpoint);

		try {
			await deviceService.sendOutputCmd(auth, output.objectID, "output", "pulse");
			this.setOutputSelected(output, false);
		} catch (err) {
			if (err) {
				outputPulseState.error = err.statusText;
			} else {
				outputPulseState.error = "Error triggering relay";
			}

			outputPulseState.sendingCommand = false;
		}
	}
}
